import React, { ReactNode, FC, memo, useContext, useMemo, Context, RefObject } from 'react';
import { ThemeContext } from 'styled-components';

import { ResponsiveProps } from 'ia-react-core/~/interfaces/ResponsiveProps';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { StyledCol } from './FlexCol.styles';
import useResponsiveProps from '../../../hooks/useResponsiveProps';

const RESPONSIVE_PROPS = ['col', 'minColWidth', 'width'] as const;

type FlexColResponsiveProps = {
  /** Width of the column, in 1/12 of its row. */
  col?: number;
  /** Minimum width in pixel until the column collapses to the next row. */
  minColWidth?: number;
  /** A number in % to override the with based on the col, or a string for a custom size */
  width?: number | string;
};

export interface FlexColProps extends
  FlexColResponsiveProps,
  ResponsiveProps<FlexColResponsiveProps>,
  SCP<'div'> {
  /** Content of the column. Usually a type of control. */
  children: ReactNode;
  /** Unique id for the col. */
  colId?: string;
  /** Width of the column, in 1/12 of its row. */
  col?: number;
  /** Minimum width in pixel until the column collapses to the next row. */
  minColWidth?: number;
  /** A number to in % to override the with based on the col, or a string for a custom size */
  width?: number | string;
  /** The Context to read from and expose the columns to. */
  ColContext: Context<{
    defaultCol?: number;
    minColWidth?: number;
    gutterWidth?: number;
  }>;
  /** Ref to the column element. */
  colRef?: RefObject<HTMLDivElement>;
  /** Label of the column to expose on the context. */
  label?: ReactNode;
}

const FlexCol: FC<FlexColProps> = memo(({ children, colId, label, ColContext, colRef, ...props }) => {
  const { minColWidth } = props;
  const {
    layout: {
      defaultCol: baseDefaultCol,
      defaultColWidth,
    },
  } = useContext(ThemeContext);

  const {
    defaultCol = baseDefaultCol,
    minColWidth: parentMinColWidth = defaultColWidth,
    gutterWidth,
    ...colContext
  } = useContext(ColContext) || {};

  const newFormContext = useMemo(() => ({
    defaultCol,
    minColWidth: minColWidth || parentMinColWidth,
    label,
    ...colContext,
    colId,
  }), [defaultCol, minColWidth, parentMinColWidth, label, colContext, colId]);

  const resolvedCols = useResponsiveProps(props, RESPONSIVE_PROPS);

  return (
    <ColContext.Provider
      value={newFormContext}
    >
      <StyledCol
        {...props}
        ref={colRef}
        col={resolvedCols.col || defaultCol}
        minColWidth={resolvedCols.minColWidth || parentMinColWidth}
        width={resolvedCols.width}
        gutterWidth={gutterWidth}
      >
        {children}
      </StyledCol>
    </ColContext.Provider>
  );
});

export default FlexCol;
